import { type ReactElement } from 'react'
import { getMillisFromDateValue } from '@util'
import { type UnsavedQuiz } from '@terros/common'
import { type UseFormReturnType } from '@mantine/form'
import { DateTimePicker } from '@mantine/dates'
import { Group, NumberInput, Select, Stack, TextInput } from '@mantine/core'
import { Size } from '@front/model'
import { FormBlocker, TSection } from '@components'
import { QuizEditorsSection } from './QuizEditorsSection'

type QuizFormProps = {
  form: UseFormReturnType<UnsavedQuiz>
}

const DATETIME_FORMAT = 'MMM DD, YYYY hh:mm A'

export const QuizForm = ({ form }: QuizFormProps): ReactElement => {
  return (
    <Stack gap={Size.space['2xl']}>
      <FormBlocker form={form} />
      <TSection title='Basic Information' heading={{ title: 'Basic Information' }}>
        <Stack gap='md'>
          <TextInput label='Quiz Title' placeholder='Enter quiz title' required {...form.getInputProps('title')} />

          <Group grow>
            <DateTimePicker
              {...form.getInputProps('startDate')}
              label='Activation Date'
              placeholder='Select activation date'
              required
              value={new Date(form.values.startDate)}
              valueFormat={DATETIME_FORMAT}
              timePickerProps={{ format: '12h' }}
              onChange={(date) => {
                const millis = getMillisFromDateValue(date)
                if (!millis) return
                form.setFieldValue('startDate', millis)
              }}
            />
            <DateTimePicker
              {...form.getInputProps('endDate')}
              label='Deactivation Date'
              placeholder='Select deactivation date'
              required
              value={new Date(form.values.endDate)}
              valueFormat={DATETIME_FORMAT}
              timePickerProps={{ format: '12h' }}
              onChange={(date) => {
                const millis = getMillisFromDateValue(date)
                if (!millis) return
                form.setFieldValue('endDate', millis)
              }}
            />
          </Group>
        </Stack>
      </TSection>

      <TSection title='Quiz Options' heading={{ title: 'Quiz Options' }}>
        <Stack gap='md'>
          <Select
            {...form.getInputProps('options.sorting')}
            label='Question Order'
            placeholder='Select question order'
            value={form.values.options.sorting ?? 'sorted'}
            data={[
              { value: 'random', label: 'Random' },
              { value: 'sorted', label: 'As Ordered' },
            ]}
          />

          <NumberInput
            {...form.getInputProps('options.maxQuestions')}
            label='Max Questions (Optional)'
            placeholder='All questions'
            min={1}
          />

          <NumberInput
            {...form.getInputProps('options.maxMinutesToComplete')}
            label='Time to Complete'
            placeholder='Time limit in minutes'
            suffix=' minutes'
            min={1}
          />

          <NumberInput
            {...form.getInputProps('options.minMinutesBetweenAttempts')}
            label='Minimum Time Between Attempts'
            placeholder='Time between attempts in minutes'
            suffix=' minutes'
            min={0}
          />
        </Stack>
      </TSection>

      <TSection title='Scoring' heading={{ title: 'Scoring' }}>
        <Stack gap='md'>
          <NumberInput
            {...form.getInputProps('options.minimumScore')}
            label='Minimum Score to Pass'
            placeholder='Enter minimum passing score'
            required
            min={0}
            max={100}
            suffix='%'
          />

          <NumberInput
            {...form.getInputProps('options.minimumPercentToRevealWrongQuestions')}
            label='Minimum Score to Reveal Wrong Questions'
            placeholder='Enter minimum percent'
            required
            min={0}
            max={100}
            suffix='%'
          />
        </Stack>
      </TSection>

      <QuizEditorsSection form={form} />
    </Stack>
  )
}
